import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { ErrorText, FlowTitle } from '@/components/ui/text';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, PaperPlaneTiltIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/cms/ui/dialog';
import { useSceneHealth } from '@/hooks/useSceneHealth';
import { featureBlockPhrase, usePublishingMatrix } from '@/modules/sceneHealth';
import { useUnavailableReason } from '@/modules/serviceMonitor';
import { SceneContext } from '@/hooks/SceneProvider';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { storyPatchBody } from '@/infrastructure/api/v1/mappers/stories';
import { sanitizeLocationText } from '@/utils/locationUtils';
import type { ChannelVerdict } from '@/modules/sceneHealth';
import type { PublishDevice } from './types';
import { usePublishFlow } from './usePublishFlow';
import { usePublishFlowImages } from './usePublishFlowImages';
import { usePublishFlowProject } from './usePublishFlowProject';
import { useTemplateMetadata } from './useTemplateMetadata';
import { templateRequirementsReason, useTemplateSceneSignals } from './useTemplateSceneSignals';
import { StepIndicator } from './StepIndicator';
import { ChoosePlatformStep } from './steps/ChoosePlatformStep';
import { useStoryVisibilityChoice } from './useStoryVisibilityChoice';
import { ImmersalTokenOverlay } from './ImmersalTokenOverlay';
import { ProjectInfoStep } from './steps/ProjectInfoStep';
import { SceneInfoStep } from './steps/SceneInfoStep';
import { AnchorInfoStep } from './steps/AnchorInfoStep';
import { ConfirmationStep } from './steps/ConfirmationStep';
import { TemplateInfoStep } from './steps/TemplateInfoStep';
import { TemplateMediaStep } from './steps/TemplateMediaStep';
import { TemplateFeaturesStep } from './steps/TemplateFeaturesStep';
import { TemplateConfirmStep } from './steps/TemplateConfirmStep';
import {
  ANCHOR_CATEGORY_TO_TEMPLATE_CATEGORY,
  MAX_TEMPLATE_TAGS,
  MIN_TEMPLATE_DESCRIPTION_LENGTH,
} from './templateOptions';
import { getMissingFields, isEmailLike, withoutKeys, type FieldKey, type PublishFlowValues } from './validation';
import type {
  AnchorCategory,
  PublishDestination,
  PublishFlowSubmitArgs,
  PublishVersionOption,
  StepId,
  TemplateDifficulty,
} from './types';
import { PublishValidationError } from './types';
import { WithTooltip } from '@/components/ui/tooltip';

interface PublishFlowModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  storyName: string;
  creatorName: string;
  anchorCategory: AnchorCategory | undefined;
  projectInstanceId?: string | null;
  sceneOriginId?: string | null;
  sceneInstanceId?: string | null;
  // The scene's auto-captured screenshot, used as the cover-generation seed.
  sceneCoverImgUrl?: string | null;

  // Versions of this story the creator can choose between. When launched from the
  // editor (lockVersionSelection), the currently-open version is pre-selected and
  // can't be changed. With a single publishable version it is auto-selected.
  versions?: PublishVersionOption[];
  lockVersionSelection?: boolean;
  // Opens the modal already on a path. Used by "Update submission", which is
  // resubmitting a template rather than starting a fresh publish.
  initialDestination?: PublishDestination;
  // Opens the template details panel after a successful template submit. Present
  // only when there is a template to show, so the creator never leaves the story.
  onViewTemplate?: () => void;

  initialProjectName?: string;
  initialProjectDescription?: string;
  initialProjectTags?: string[];
  initialAddress?: string;
  initialLatitude?: string;
  initialLongitude?: string;
  initialCity?: string;
  initialSceneIntroduction?: string;

  onSubmit: (args: PublishFlowSubmitArgs) => Promise<void>;

  // Opens the in-editor test (share/QR) dialog. Surfaced for place-anywhere
  // scenes, which can't be published but can still be tested.
  onRequestTest?: () => void;
}

const TEMPLATE_FIELD_TOOLTIPS: Record<
  'templateName' | 'templateDescription' | 'templateLearnings' | 'templateDifficulty' | 'templateTags' | 'templateFeatures' | 'templateVideo' | 'templateCover',
  string
> = {
  templateName: 'Name your template',
  templateDescription: `Write a ${MIN_TEMPLATE_DESCRIPTION_LENGTH}-character description`,
  templateLearnings: 'Say what creators will learn',
  templateDifficulty: 'Pick a difficulty',
  templateTags: `Add 1 to ${MAX_TEMPLATE_TAGS} tags`,
  templateFeatures: 'Tick the features it shows off',
  templateVideo: 'Upload a video',
  templateCover: 'Add a cover image',
};

const FIELD_TOOLTIPS_ADD: Record<FieldKey, string> = {
  storyName: 'Add a story name',
  creatorName: 'Add a creator name',
  projectName: 'Add a project name',
  projectDescription: 'Add a project description',
  projectTags: 'Add at least one tag',
  projectImages: 'Add a portrait and landscape cover image',
  anchorImage: 'Upload an anchor image',
  anchorAddress: 'Enter a real address',
  ...TEMPLATE_FIELD_TOOLTIPS,
};

const FIELD_TOOLTIPS_RENAME: Record<FieldKey, string> = {
  storyName: 'Rename your story',
  creatorName: 'Add a creator name',
  projectName: 'Rename your project',
  projectDescription: 'Add a project description',
  projectTags: 'Add at least one tag',
  projectImages: 'Add a portrait and landscape cover image',
  anchorImage: 'Upload an anchor image',
  anchorAddress: 'Enter a real address',
  ...TEMPLATE_FIELD_TOOLTIPS,
};

function tooltipForField(key: FieldKey, values: PublishFlowValues): string {
  if (key === 'storyName') {
    return values.storyName.trim().length > 0
      ? FIELD_TOOLTIPS_RENAME[key]
      : FIELD_TOOLTIPS_ADD[key];
  }
  if (key === 'creatorName') {
    return isEmailLike(values.creatorName)
      ? "Creator name can't be an email"
      : FIELD_TOOLTIPS_ADD[key];
  }
  if (key === 'projectName') {
    return values.projectName.trim().length > 0
      ? FIELD_TOOLTIPS_RENAME[key]
      : FIELD_TOOLTIPS_ADD[key];
  }
  return FIELD_TOOLTIPS_ADD[key];
}

function missingFieldsTooltip(missing: FieldKey[], values: PublishFlowValues): string {
  if (missing.length === 0) return '';
  if (missing.length === 1) return tooltipForField(missing[0], values);
  return `Required: ${missing.map((k) => tooltipForField(k, values)).join(', ')}`;
}

const STEP_TITLES: Record<StepId, string> = {
  'choose-platform': 'Choose where to publish',
  'project-info': 'Project info',
  'scene-info': 'Story info',
  'anchor-info': 'Anchor info',
  confirmation: 'Confirm and submit',
  'template-info': 'Template info',
  'template-features': 'Features and tags',
  'template-media': 'Template media',
  'template-confirm': 'Confirm and submit',
};

export function PublishFlowModal({
  open,
  onOpenChange,
  storyName: initialStoryName,
  creatorName: initialCreatorName,
  anchorCategory,
  projectInstanceId,
  sceneOriginId,
  sceneInstanceId,
  sceneCoverImgUrl,
  versions = [],
  lockVersionSelection = false,
  initialDestination = 'audience',
  onViewTemplate,
  initialProjectName = '',
  initialProjectDescription = '',
  initialProjectTags = [],
  initialAddress = '',
  initialLatitude = '',
  initialLongitude = '',
  initialCity = '',
  initialSceneIntroduction = '',
  onSubmit,
  onRequestTest,
}: PublishFlowModalProps) {
  const navigate = useNavigate();
  const services = useServices();

  // Default to the entry point's current version when it is publishable (or when
  // the selection is locked), otherwise fall back to the first publishable one.
  const resolveInitialVersionId = (): string | null => {
    const current = versions.find((v) => v.sceneInstanceId === sceneInstanceId);
    if (current && (current.selectable || lockVersionSelection)) return current.sceneInstanceId;
    return versions.find((v) => v.selectable)?.sceneInstanceId ?? sceneInstanceId ?? null;
  };
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(resolveInitialVersionId);
  const effectiveSceneInstanceId = selectedVersionId ?? sceneInstanceId ?? null;

  const images = usePublishFlowImages({ projectInstanceId, sceneOriginId, sceneInstanceId: effectiveSceneInstanceId, enabled: open });
  const scene = useContext(SceneContext);
  const sceneAnchorCategory = scene?.anchorCategory ?? null;
  const refreshAnchorCategory = scene?.refreshAnchorCategory;
  useEffect(() => {
    if (open) void refreshAnchorCategory?.();
  }, [open, refreshAnchorCategory]);
  const effectiveAnchorCategory = images.anchorCategory ?? sceneAnchorCategory ?? anchorCategory;
  const flow = usePublishFlow({
    anchorCategory: effectiveAnchorCategory ?? undefined,
    initialDestination,
    // A template entry point ("Update submission") has already made the
    // destination choice, so land straight on the template details.
    initialStep: initialDestination === 'template' ? 'template-info' : undefined,
  });

  const { data: sceneHealth, loading: sceneHealthLoading, failed: sceneHealthFailed, refetch: recheckSceneHealth } = useSceneHealth(open ? effectiveSceneInstanceId : null);
  // Kept for its fetch of the shared publishing matrix; the value isn't read here.
  usePublishingMatrix();

  const sceneHealthBlockedReasons = useMemo(() => {
    const m = new Map<PublishDevice, string>();
    if (!sceneHealth) return m;
    const verdict = (key: string) => sceneHealth.verdicts.find((v) => v.key === key);
    const formatReason = (v: ChannelVerdict): string => {
      const r = v.reasons[0];
      if (!r) return 'Blocked';
      if (r.type === 'feature_blocked' && r.feature) {
        return `Blocked because ${featureBlockPhrase(r.feature)}`;
      }
      if (r.type === 'size_hard' && r.thresholdMB != null) {
        return `Blocked because story is over ${r.thresholdMB} MB`;
      }
      return 'Blocked';
    };
    const native_ios = verdict('native_ios');
    const native_android = verdict('native_android');
    if (native_ios?.status === 'no' && native_android?.status === 'no') {
      m.set('app', formatReason(native_ios));
    }
    const appless_ios = verdict('appless_ios');
    if (appless_ios?.status === 'no') {
      m.set('appless', formatReason(appless_ios));
    }
    const specs = verdict('spectacles');
    if (specs?.status === 'no') m.set('spectacles', formatReason(specs));
    return m;
  }, [sceneHealth]);
  const storyIds = useMemo(() => (sceneOriginId ? [sceneOriginId] : []), [sceneOriginId]);
  const visibilityChoice = useStoryVisibilityChoice(storyIds, open);
  const fetchedProject = usePublishFlowProject(projectInstanceId, open);

  // Creator name is auto-filled from the user's profile, which can fall back to
  // their email. An email isn't a valid creator name, so don't seed it.
  const seededCreatorName = isEmailLike(initialCreatorName) ? '' : initialCreatorName;

  const [storyName, setStoryName] = useState(initialStoryName);
  const [creatorName, setCreatorName] = useState(seededCreatorName);

  const [projectName, setProjectName] = useState(initialProjectName);
  const [projectDescription, setProjectDescription] = useState(initialProjectDescription);
  const [projectTags, setProjectTags] = useState<string[]>(initialProjectTags);

  const [sceneIntroduction, setSceneIntroduction] = useState(initialSceneIntroduction);

  const [anchorAddress, setAnchorAddress] = useState(sanitizeLocationText(initialAddress));
  const [anchorLat, setAnchorLat] = useState(initialLatitude);
  const [anchorLng, setAnchorLng] = useState(initialLongitude);
  const [anchorCity, setAnchorCity] = useState(sanitizeLocationText(initialCity));

  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateLearnings, setTemplateLearnings] = useState('');
  const [templateDifficulty, setTemplateDifficulty] = useState<TemplateDifficulty | ''>('');
  const [templateTags, setTemplateTags] = useState<string[]>([]);
  const [templateFeatures, setTemplateFeatures] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState(false);
  // Offline, the flow can't go forward or submit: each step reads from Lureo's servers, and submitting writes to them
  const unavailable = useUnavailableReason();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showTokenOverlay, setShowTokenOverlay] = useState(false);

  const [errors, setErrors] = useState<Set<FieldKey>>(new Set());
  const [visited, setVisited] = useState<Set<StepId>>(new Set());
  const [attempted, setAttempted] = useState<Set<StepId>>(new Set());
  const projectSeeded = useRef(false);
  const anchorLocationSeeded = useRef(false);
  const [templateSeeded, setTemplateSeeded] = useState(false);
  const metadataWrite = useRef<Promise<unknown>>(Promise.resolve());

  // Seed on open (not close) so props that changed while the modal was closed,
  // like a renamed story or an updated intro, are what the creator sees.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (open) {
      setSubmitting(false);
      setSubmitError(null);
      setSubmitted(false);
      setErrors(new Set());
      setVisited(new Set());
      setAttempted(new Set());
      setTemplateSeeded(false);
      setTemplateName('');
      setTemplateDescription('');
      setTemplateDifficulty('');
      setTemplateTags([]);
      setTemplateLearnings('');
      setTemplateFeatures([]);
      setSelectedVersionId(resolveInitialVersionId());
      setStoryName(initialStoryName);
      setCreatorName(seededCreatorName);
      setProjectName(initialProjectName);
      setProjectDescription(initialProjectDescription);
      setProjectTags(initialProjectTags);
      setSceneIntroduction(initialSceneIntroduction);
      // A story saved with no address reads back as the literal "null" placeholder,
      // which must not land in the field: blanking it also lets the map's
      // reverse-geocode fill in the real address.
      setAnchorAddress(sanitizeLocationText(initialAddress));
      setAnchorLat(initialLatitude);
      setAnchorLng(initialLongitude);
      setAnchorCity(sanitizeLocationText(initialCity));
    }
  }
  useEffect(() => {
    if (!open) return;
    flow.reset();
    visibilityChoice.reset();
    projectSeeded.current = false;
    anchorLocationSeeded.current = false;
    // We intentionally only react to `open` here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Each step arrival while open marks the step visited.
  const [visitedKey, setVisitedKey] = useState<{ open: boolean; step: StepId } | null>(null);
  if (!visitedKey || visitedKey.open !== open || visitedKey.step !== flow.currentStep) {
    setVisitedKey({ open, step: flow.currentStep });
    if (open) setVisited((prev) => (prev.has(flow.currentStep) ? prev : new Set([...prev, flow.currentStep])));
  }

  useEffect(() => {
    if (!open) return;
    services.analytics.trackLureoPublishFlowStepViewed({
      destination: flow.destination,
      step: flow.currentStep,
      step_index: flow.currentStepIndex,
      step_count: flow.visibleSteps.length,
    });
    // One event per step arrival. Switching destination reshapes the same step, so
    // it must not re-fire; the other flow fields are read-only context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flow.currentStep]);

  // Seed project fields from the DB once it loads (covers the editor entry point,
  // which has no project loaded). Guarded so it doesn't clobber in-progress edits.
  useEffect(() => {
    if (open && fetchedProject && !projectSeeded.current) {
      projectSeeded.current = true;
      setProjectName(fetchedProject.name);
      setProjectDescription(fetchedProject.description);
      setProjectTags(fetchedProject.tags);
    }
  }, [open, fetchedProject]);

  // Switching the chosen version re-keys the anchor/cover data, so allow the
  // anchor coordinates to re-seed from the newly selected version.
  useEffect(() => {
    anchorLocationSeeded.current = false;
  }, [effectiveSceneInstanceId]);

  // Seed coordinates from the anchor's own asset_instance lat/lng once it loads.
  useEffect(() => {
    if (open && images.anchorLatitude != null && !anchorLocationSeeded.current) {
      anchorLocationSeeded.current = true;
      setAnchorLat(String(images.anchorLatitude));
      setAnchorLng(images.anchorLongitude != null ? String(images.anchorLongitude) : '');
    }
  }, [open, images.anchorLatitude, images.anchorLongitude]);

  const templateMetadata = useTemplateMetadata(sceneOriginId, open);
  const templateSignals = useTemplateSceneSignals(effectiveSceneInstanceId, open);
  const templateGateReason = templateRequirementsReason(templateSignals);

  if (open && templateMetadata && !templateSeeded) {
    setTemplateSeeded(true);
    setTemplateName((prev) => prev || templateMetadata.name || storyName);
    setTemplateDescription((prev) => prev || templateMetadata.description || initialSceneIntroduction);
    setTemplateDifficulty((prev) => prev || templateMetadata.difficulty);
    setTemplateTags((prev) => (prev.length > 0 ? prev : templateMetadata.tags));
    setTemplateLearnings((prev) => prev || templateMetadata.learnings);
    setTemplateFeatures((prev) => (prev.length > 0 ? prev : templateMetadata.features));
  }

  // Pre-select the inferred difficulty. Gated on the saved metadata having resolved
  // so a creator's own earlier pick always wins the race against the suggestion.
  // Sits after the seeding above so the saved pick is applied first.
  const suggestedDifficulty = templateSignals.suggestedDifficulty;
  const [suggestionKey, setSuggestionKey] = useState<{ open: boolean; metadata: typeof templateMetadata; suggested: typeof suggestedDifficulty } | null>(null);
  if (
    !suggestionKey ||
    suggestionKey.open !== open ||
    suggestionKey.metadata !== templateMetadata ||
    suggestionKey.suggested !== suggestedDifficulty
  ) {
    setSuggestionKey({ open, metadata: templateMetadata, suggested: suggestedDifficulty });
    if (open && suggestedDifficulty && templateMetadata) setTemplateDifficulty((prev) => prev || suggestedDifficulty);
  }

  const stepTitle = STEP_TITLES[flow.currentStep];
  // Derived from the scene's anchor rather than chosen: the gallery facet is a fact
  // about the story, so a creator picking a mismatched one would only break the
  // gallery's own filtering. Recomputes when the anchor finishes loading.
  const templateCategory = effectiveAnchorCategory
    ? ANCHOR_CATEGORY_TO_TEMPLATE_CATEGORY[effectiveAnchorCategory] ?? ''
    : '';

  const isConfirmStep = flow.currentStep === 'confirmation' || flow.currentStep === 'template-confirm';

  const isTemplateResubmission =
    templateMetadata?.submissionStatus === 'submitted' ||
    templateMetadata?.submissionStatus === 'rejected';

  // Place-anywhere scenes have no anchor and can't be submitted for review; the
  // creator must add an anchor first (they're still free to test the scene).
  const isPlaceAnywhere = effectiveAnchorCategory === 'place_anywhere';

  const anchorImageUrl = effectiveAnchorCategory === 'image_anchor' ? images.imageAnchorUrl : images.scanImageUrl;
  const anchorThumbnailUnchanged = effectiveAnchorCategory === 'immersal' && images.scanImageIsAutoThumbnail;
  const values: PublishFlowValues = {
    storyName,
    creatorName,
    projectName,
    projectDescription,
    projectTags,
    hasProjectPortrait: Boolean(images.projectPortrait.url),
    hasProjectLandscape: Boolean(images.projectLandscape.url ?? images.sceneLandscape.url),
    hasAnchorImage: Boolean(anchorImageUrl),
    anchorThumbnailUnchanged,
    anchorAddress,
    templateName,
    templateDescription,
    templateLearnings,
    templateDifficulty,
    templateTags,
    templateFeatures,
    hasTemplateVideo: Boolean(images.templateVideoUrl),
    // The media step no longer shows the story cover as a stand-in, so the template
    // needs a cover row of its own before it can be submitted.
    hasTemplateCover: Boolean(images.templateCoverOwn?.url),
  };

  // Clear a standing anchor-image error once the creator supplies a valid image,
  // so the warning doesn't linger after they replace the Immersal thumbnail.
  if (errors.has('anchorImage') && values.hasAnchorImage && !anchorThumbnailUnchanged) {
    setErrors((prev) => withoutKeys(prev, 'anchorImage'));
  }

  if (errors.has('projectImages') && values.hasProjectPortrait && values.hasProjectLandscape) {
    setErrors((prev) => withoutKeys(prev, 'projectImages'));
  }

  // Media uploads land asynchronously, so clear their errors as the rows appear
  // rather than making the creator press Next again to notice.
  if (errors.has('templateVideo') && values.hasTemplateVideo) {
    setErrors((prev) => withoutKeys(prev, 'templateVideo'));
  }

  if (errors.has('templateCover') && values.hasTemplateCover) {
    setErrors((prev) => withoutKeys(prev, 'templateCover'));
  }

  const missingByStep = new Map<StepId, FieldKey[]>();
  for (const step of flow.visibleSteps) {
    missingByStep.set(step, getMissingFields(step, values, flow.destination));
  }
  const missingForCurrentStep = missingByStep.get(flow.currentStep) ?? [];
  const isTemplatePath = flow.destination === 'template';
  // Next waits for the story's health, which says where it can publish; a check that failed is asked again from the step
  const sceneHealthGate = flow.currentStep === 'choose-platform' && !isTemplatePath && (sceneHealthLoading || sceneHealthFailed);
  const noPlatform = !isTemplatePath && flow.devices.length === 0;
  const platformGateForStep = (id: StepId): boolean => id === 'choose-platform' && noPlatform;

  const blockReasonsFor = (stepId: StepId): string[] => {
    const reasons: string[] = [];
    if (platformGateForStep(stepId)) reasons.push('Choose where to publish');
    // Keeps the stepper from jumping a gated template story straight to the
    // template details, where the block would have nowhere to show itself.
    if (stepId === 'choose-platform' && isTemplatePath && templateGateReason) {
      reasons.push(templateGateReason);
    }
    const missing = missingByStep.get(stepId) ?? [];
    if (missing.length > 0) reasons.push(missingFieldsTooltip(missing, values));
    return reasons;
  };

  const stepFullyDone = (id: StepId): boolean => blockReasonsFor(id).length === 0;

  const currentBlockReasons = blockReasonsFor(flow.currentStep);

  const stepNavReason = useCallback(
    (target: StepId): string | undefined => {
      const targetIdx = flow.visibleSteps.indexOf(target);
      if (targetIdx === -1 || targetIdx <= flow.currentStepIndex) return undefined;
      // Offline the flow only goes back, as Next does
      if (unavailable) return unavailable;
      const isAdjacent = targetIdx === flow.currentStepIndex + 1;
      let anyIncomplete = false;
      let firstReasons: string[] | undefined;
      for (let i = flow.currentStepIndex; i < targetIdx; i++) {
        const stepId = flow.visibleSteps[i];
        const reasons = blockReasonsFor(stepId);
        if (reasons.length > 0) {
          firstReasons = reasons;
          anyIncomplete = true;
          break;
        }
      }
      if (!anyIncomplete) return undefined;
      if (isAdjacent && firstReasons) return firstReasons.join(' · ');
      return 'Finish previous steps';
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      flow.visibleSteps,
      flow.currentStepIndex,
      missingByStep,
      values,
      noPlatform,
      isTemplatePath,
      templateGateReason,
      unavailable,
    ],
  );

  // Template picks are saved as the creator leaves either template metadata step, so
  // closing the modal and coming back doesn't cost them the category, difficulty, tags,
  // features or learnings. The description is not stored here: scene_origin_description
  // is the story's own audience-facing text, and only the template copy carries the
  // template one.
  //
  // Each step writes only the columns it owns, and the writes are queued rather than
  // fired in parallel. Both matter because these are fire-and-forget: a full-row write
  // from Template info that landed after one from Features would put its older snapshot
  // of tags and features back.
  const persistTemplateInfo = () => {
    if (!sceneOriginId) return;
    // Until the saved metadata has landed, this session's state is still empty, and
    // writing it would blank a resubmission's existing values.
    if (!templateSeeded) return;

    const patch =
      flow.currentStep === 'template-info'
        ? {
            ...(templateCategory ? { scene_origin_category: templateCategory } : {}),
            scene_origin_difficulty: templateDifficulty || null,
            scene_origin_learnings: templateLearnings || null,
          }
        : flow.currentStep === 'template-features'
          ? {
              scene_origin_tags: templateTags.length > 0 ? templateTags : null,
              scene_origin_features: templateFeatures.length > 0 ? templateFeatures : null,
            }
          : null;
    if (!patch) return;

    // The patch is snapshotted here, at the creator's action; only the request waits.
    metadataWrite.current = metadataWrite.current
      .then(() =>
        toResult(v1.PATCH('/api/v1/stories/{storyUniqueId}', {
          params: { path: { storyUniqueId: sceneOriginId } },
          body: storyPatchBody(patch),
        })),
      )
      .then((result) => {
        if (result.error) console.error('Failed to save template details', result.error);
      })
      // Swallowed so one failed write doesn't break the chain for the next.
      .catch((error: unknown) => console.error('Failed to save template details', error));
  };

  const handleNext = () => {
    if (sceneHealthGate) return;
    // The template tile stays pickable for a story that doesn't qualify; the
    // requirements only surface here, as a warning, when they try to move on.
    if (isTemplatePath && templateGateReason) {
      setAttempted((prev) => new Set(prev).add(flow.currentStep));
      if (missingForCurrentStep.length > 0) setErrors(new Set(missingForCurrentStep));
      return;
    }
    if (noPlatform || missingForCurrentStep.length > 0) {
      setAttempted((prev) => new Set(prev).add(flow.currentStep));
      if (missingForCurrentStep.length > 0) setErrors(new Set(missingForCurrentStep));
      return;
    }
    persistTemplateInfo();
    setErrors(new Set());
    flow.goNext();
  };

  const handleBack = () => {
    persistTemplateInfo();
    setErrors(new Set());
    flow.goBack();
  };

  const handleStepClick = (id: StepId) => {
    persistTemplateInfo();
    setErrors(new Set());
    flow.goToStep(id);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const visible = new Set(flow.visibleSteps);
      services.analytics.trackLureoPublishFlowSubmitted({
        destination: flow.destination,
        devices: flow.devices,
        scene_instance_id: effectiveSceneInstanceId ?? undefined,
      });
      await onSubmit({
        destination: flow.destination,
        devices: flow.devices,
        sceneInstanceId: effectiveSceneInstanceId ?? undefined,
        // Story name + creator name live on the always-visible first step.
        storyName,
        creatorName,
        ...(visible.has('project-info')
          ? { projectName, projectDescription, projectTags }
          : {}),
        ...(flow.destination === 'audience' ? { visibility: visibilityChoice.visibility } : {}),
        ...(visible.has('scene-info') ? { sceneIntroduction } : {}),
        ...(visible.has('anchor-info')
          ? { address: anchorAddress, latitude: anchorLat, longitude: anchorLng, city: anchorCity }
          : {}),
        ...(visible.has('template-info')
          ? {
              templateName,
              templateDescription,
              templateLearnings,
              templateCategory,
              templateDifficulty: templateDifficulty || undefined,
            }
          : {}),
        ...(visible.has('template-features') ? { templateTags, templateFeatures } : {}),
      });
      setSubmitted(true);
    } catch (e) {
      // Validation failures are already surfaced (Missing Required Information
      // dialog), so don't show the success screen or a generic error; the modal
      // stays open on the current step.
      if (!(e instanceof PublishValidationError)) {
        setSubmitError(e instanceof Error ? e.message : 'Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Live scenes can't be edited in the spatial editor, so after submitting we send
  // the creator to the CMS story-edit page rather than back into the editor.
  const handleExit = () => {
    onOpenChange(false);
    if (projectInstanceId && sceneOriginId) {
      navigate(`/story/edit/${sceneOriginId}`);
    }
  };

  const body = useMemo(() => {
    switch (flow.currentStep) {
      case 'choose-platform':
        if (sceneHealthLoading && !isTemplatePath) {
          return (
            <div className="h-full flex items-center justify-center">
              <LoadingSpinner size="md" className="h-6 w-6" />
            </div>
          );
        }
        if (sceneHealthFailed && !isTemplatePath) {
          return (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <ErrorText>Couldn't check this story's health. Check your connection and try again.</ErrorText>
              <LureoButton
                onClick={recheckSceneHealth}
                variant="outline-2"
                suppressTooltip
                className="h-9 px-5 rounded-lureo-radius-pill"
              >
                Try again
              </LureoButton>
            </div>
          );
        }
        return (
          <ChoosePlatformStep
            storyName={storyName}
            creatorName={creatorName}
            anchorCategory={effectiveAnchorCategory}
            devices={flow.devices}
            errors={errors}
            sceneHealthBlockedReasons={sceneHealthBlockedReasons}
            versions={versions}
            selectedVersionId={effectiveSceneInstanceId}
            versionLocked={lockVersionSelection}
            destination={flow.destination}
            templateBlockedReason={
              templateMetadata?.submissionStatus === 'accepted'
                ? 'This story is already a live template.'
                : undefined
            }
            templateRequirementsWarning={
              isTemplatePath && attempted.has('choose-platform') ? templateGateReason : null
            }
            onSelectTemplateDestination={flow.selectTemplateDestination}
            onRegisterTemplateInterest={() =>
              services.analytics.trackLureoTemplateInterestClicked({
                scene_instance_id: effectiveSceneInstanceId ?? undefined,
              })
            }
            onTest={onRequestTest}
            onSelectVersion={setSelectedVersionId}
            onToggleDevice={flow.toggleDevice}
            onRequestImmersalToken={() => setShowTokenOverlay(true)}
            visibility={visibilityChoice.visibility}
            onVisibilityChange={visibilityChoice.setVisibility}
            onStoryNameChange={(v) => {
              setStoryName(v);
              setErrors((prev) => withoutKeys(prev, 'storyName'));
            }}
            onCreatorNameChange={(v) => {
              setCreatorName(v);
              setErrors((prev) => withoutKeys(prev, 'creatorName'));
            }}
          />
        );
      case 'project-info':
        return (
          <ProjectInfoStep
            projectName={projectName}
            projectDescription={projectDescription}
            projectTags={projectTags}
            coverImages={images}
            projectInstanceId={projectInstanceId}
            sceneOriginId={sceneOriginId}
            sceneCoverImgUrl={sceneCoverImgUrl}
            errors={errors}
            onChange={(patch) => {
              if (patch.projectName !== undefined) {
                setProjectName(patch.projectName);
                setErrors((prev) => withoutKeys(prev, 'projectName'));
              }
              if (patch.projectDescription !== undefined) {
                setProjectDescription(patch.projectDescription);
                setErrors((prev) => withoutKeys(prev, 'projectDescription'));
              }
              if (patch.projectTags !== undefined) {
                setProjectTags(patch.projectTags);
                setErrors((prev) => withoutKeys(prev, 'projectTags'));
              }
              if (patch.storyName !== undefined) {
                setStoryName(patch.storyName);
                setErrors((prev) => withoutKeys(prev, 'storyName'));
              }
              if (patch.sceneIntroduction !== undefined) {
                setSceneIntroduction(patch.sceneIntroduction);
              }
            }}
          />
        );
      case 'scene-info':
        return (
          <SceneInfoStep
            sceneIntroduction={sceneIntroduction}
            coverImages={images}
            projectInstanceId={projectInstanceId}
            sceneOriginId={sceneOriginId}
            onChange={(patch) => {
              if (patch.sceneIntroduction !== undefined) setSceneIntroduction(patch.sceneIntroduction);
            }}
          />
        );
      case 'anchor-info':
        return (
          <AnchorInfoStep
            anchorCategory={effectiveAnchorCategory}
            coverImages={images}
            projectInstanceId={projectInstanceId}
            sceneOriginId={sceneOriginId}
            address={anchorAddress}
            latitude={anchorLat}
            longitude={anchorLng}
            errors={errors}
            onChange={(patch) => {
              if (patch.address !== undefined) {
                setAnchorAddress(patch.address);
                setErrors((prev) => withoutKeys(prev, 'anchorAddress'));
              }
              if (patch.latitude !== undefined) setAnchorLat(patch.latitude);
              if (patch.longitude !== undefined) setAnchorLng(patch.longitude);
              if (patch.city !== undefined) setAnchorCity(patch.city);
            }}
          />
        );
      case 'confirmation':
        return (
          <ConfirmationStep
            devices={flow.devices}
            storyName={storyName}
            submitted={submitted}
            sceneInstanceId={effectiveSceneInstanceId}
            coverImageUrl={images.sceneLandscape.url}
            anchorCategory={effectiveAnchorCategory ?? undefined}
          />
        );
      case 'template-info':
        return (
          <TemplateInfoStep
            name={templateName}
            storyName={storyName}
            description={templateDescription}
            difficulty={templateDifficulty}
            suggestedDifficulty={templateSignals.suggestedDifficulty}
            suggestionReason={
              templateSignals.hasMask
                ? 'Your story uses masks.'
                : templateSignals.hasAnimation
                  ? 'Your story uses keyframe animation.'
                  : 'Your story has no animations or masks.'
            }
            learnings={templateLearnings}
            errors={errors}
            onChange={(patch) => {
              if (patch.name !== undefined) {
                setTemplateName(patch.name);
                setErrors((prev) => withoutKeys(prev, 'templateName'));
              }
              if (patch.description !== undefined) {
                setTemplateDescription(patch.description);
                setErrors((prev) => withoutKeys(prev, 'templateDescription'));
              }
              if (patch.learnings !== undefined) {
                setTemplateLearnings(patch.learnings);
                setErrors((prev) => withoutKeys(prev, 'templateLearnings'));
              }
              if (patch.difficulty !== undefined) {
                setTemplateDifficulty(patch.difficulty);
                setErrors((prev) => withoutKeys(prev, 'templateDifficulty'));
              }
            }}
          />
        );
      case 'template-features':
        return (
          <TemplateFeaturesStep
            features={templateFeatures}
            tags={templateTags}
            errors={errors}
            onChange={(patch) => {
              if (patch.features !== undefined) {
                setTemplateFeatures(patch.features);
                setErrors((prev) => withoutKeys(prev, 'templateFeatures'));
              }
              if (patch.tags !== undefined) {
                setTemplateTags(patch.tags);
                setErrors((prev) => withoutKeys(prev, 'templateTags'));
              }
            }}
          />
        );
      case 'template-media':
        return (
          <TemplateMediaStep
            coverImages={images}
            projectInstanceId={projectInstanceId}
            sceneOriginId={sceneOriginId}
            errors={errors}
          />
        );
      case 'template-confirm':
        return (
          <TemplateConfirmStep
            storyName={templateName || storyName}
            creatorName={creatorName}
            description={templateDescription}
            learnings={templateLearnings}
            category={templateCategory}
            difficulty={templateDifficulty}
            tags={templateTags}
            features={templateFeatures}
            coverImageUrl={images.templateCover.url}
            submitted={submitted}
            isResubmission={isTemplateResubmission}
          />
        );
      default:
        return null;
    }
  }, [
    flow.currentStep,
    flow.destination,
    flow.devices,
    flow.toggleDevice,
    flow.selectTemplateDestination,
    storyName,
    creatorName,
    effectiveAnchorCategory,
    projectName,
    projectDescription,
    projectTags,
    images,
    sceneIntroduction,
    anchorAddress,
    anchorLat,
    anchorLng,
    templateName,
    templateDescription,
    templateLearnings,
    templateCategory,
    templateDifficulty,
    templateTags,
    templateFeatures,
    templateMetadata,
    templateSignals,
    errors,
    attempted,
    submitted,
    isTemplateResubmission,
    templateGateReason,
    effectiveSceneInstanceId,
    versions,
    lockVersionSelection,
    projectInstanceId,
    sceneOriginId,
    sceneCoverImgUrl,
    sceneHealthLoading,
    sceneHealthFailed,
    recheckSceneHealth,
    sceneHealthBlockedReasons,
    isTemplatePath,
    services.analytics,
    onRequestTest,
    visibilityChoice.visibility,
    visibilityChoice.setVisibility,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The surface over a light, blurred scrim, at a fixed height so it keeps its size from step to step */}
      <DialogContent
        width="xl"
        scrim="soft"
        className="h-[min(40rem,calc(100vh-2rem))] max-w-240 bg-lureo-surface"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="text-lureo-16">
            {flow.destination === 'template' && isTemplateResubmission ? 'Update template' : 'Publish'}
          </DialogTitle>
        </DialogHeader>

        <StepIndicator
          current={flow.currentStep}
          visibleSteps={flow.visibleSteps}
          allSteps={flow.pathSteps}
          onStepClick={handleStepClick}
          disabled={submitting || submitted}
          submitted={submitted}
          getStepDisableReason={stepNavReason}
          getStepSatisfied={(id) => {
            if (!stepFullyDone(id)) return false;
            if (id === flow.currentStep) return true;
            return visited.has(id);
          }}
        />

        <DialogBody>
          {!(submitted && isConfirmStep) && (
            <FlowTitle className="text-lureo-20">
              {stepTitle}
            </FlowTitle>
          )}
          <div className="flex-1 min-h-0">{body}</div>
        </DialogBody>

        {!submitted && (
          // Back on the left, the way forward on the right, the reason it is blocked between them
          <DialogFooter split>
            {flow.canGoBack ? (
              <LureoButton
                onClick={handleBack}
                variant="outline-2"
                suppressTooltip
                disabled={submitting}
                className="h-9 px-5 rounded-lureo-radius-button-pill"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </LureoButton>
            ) : (
              <span />
            )}

            {submitError ? (
              <p className="font-lureo-font-prose text-lureo-12 text-lureo-danger-ink leading-[1.3] mx-3 truncate">
                {submitError}
              </p>
            ) : sceneHealthGate && sceneHealthFailed ? (
              <p className="font-lureo-font-prose text-lureo-12 text-lureo-danger-ink leading-[1.3] mx-3 truncate">
                Couldn't check story health
              </p>
            ) : sceneHealthGate ? (
              <p className="font-lureo-font-prose text-lureo-13 text-lureo-ink-subtlest leading-[1.3] mx-3 truncate">
                Checking story health…
              </p>
            ) : isTemplatePath && templateGateReason && isConfirmStep ? (
              <WithTooltip content={templateGateReason} wrap>
                <p className="font-lureo-font-prose text-lureo-12 text-lureo-danger-ink leading-[1.3] mx-3">{templateGateReason}</p>
              </WithTooltip>
            ) : attempted.has(flow.currentStep) && currentBlockReasons.length > 0 ? (
              <p className="font-lureo-font-prose text-lureo-12 text-lureo-danger-ink leading-[1.3] mx-3 truncate">
                {currentBlockReasons.join(' · ')}
              </p>
            ) : null}

            {isConfirmStep ? (
              <LureoButton
                onClick={handleSubmit}
                variant="secondary"
                suppressTooltip
                disabled={submitting || noPlatform || (isTemplatePath && !!templateGateReason)}
                disabledReason={unavailable}
                className="h-9 px-4 rounded-lureo-radius-button-pill"
              >
                <PaperPlaneTiltIcon className="h-4 w-4 shrink-0" />
                {submitting ? 'Submitting…' : 'Submit for review'}
              </LureoButton>
            ) : (
              <LureoButton
                onClick={handleNext}
                variant="secondary"
                suppressTooltip
                disabled={submitting || sceneHealthGate || (isPlaceAnywhere && !isTemplatePath)}
                disabledReason={unavailable}
                className="h-9 px-4 rounded-lureo-radius-button-pill"
              >
                Next
                <ArrowRightIcon className="h-4 w-4" />
              </LureoButton>
            )}
          </DialogFooter>
        )}

        {submitted && (
          <DialogFooter>
            {isTemplatePath && onViewTemplate && (
              <LureoButton
                variant="outline-2"
                suppressTooltip
                onClick={onViewTemplate}
                className="h-9 px-5 rounded-lureo-radius-button-pill"
              >
                View your template
              </LureoButton>
            )}
            <LureoButton
              onClick={handleExit}
              variant="secondary"
              suppressTooltip
              className="h-9 px-4 rounded-lureo-radius-button-pill"
            >
              Exit
            </LureoButton>
          </DialogFooter>
        )}

        {showTokenOverlay && (
          <ImmersalTokenOverlay onClose={() => setShowTokenOverlay(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}
